import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router';
import boxingImg from '../media/papers/ufc/gloves_computer.jpg';

import airplaneImg from '../media/papers/cognitive_partner/airplane.jpeg';
import allostericImg from '../media/papers/allosteric/cover.png';
import allosteric2Img from '../media/papers/allosteric2/cover.png';
import allosteric3Img from '../media/papers/allosteric3/cover.jpeg';
import spikeBnAbImg from '../media/papers/spike_bnab/cover.jpeg';


// Define array of papers
const papers = [
    {
        id: 6,
        name: "SARS-CoV-2 Antibody Immune Escape",
        displayTitle: "SARS-CoV-2\nAntibody Immune Escape",
        category: "Computational Biology",
        year: "2026",
        format: "MDPI",
        description: "Mutational profiling and energy landscape analysis of broadly neutralizing antibodies targeting conserved SARS-CoV-2 spike epitopes.",
        posterImage: spikeBnAbImg,
    },
    {
        id: 5,
        name: "Decoding Allosteric Grammar with Explainable AI",
        displayTitle: "Decoding Allosteric Grammar\nwith Explainable AI",
        category: "Computational Biology",
        year: "2026",
        format: "bioRxiv",
        description: "Explainable AI framework using PLMs and energy landscape frustration analysis across 453 human kinases to decode orthosteric vs allosteric detectability.",
        posterImage: allosteric3Img,
    },
    {
        id: 4,
        name: "AI Co-Folding Models for Allosteric Binding",
        displayTitle: "AI Co-Folding Models\nfor Allosteric Binding",
        category: "Computational Biology",
        year: "2026",
        format: "SSRN",
        description: "Systematic interrogation of AlphaFold3, Protenix, Boltz-2, Chai-1, and DynamicBind using physics-based explainable AI.",
        posterImage: allosteric2Img,
    },
    {
        id: 3,
        name: "Predicting and Decoding Allosteric Binding",
        displayTitle: "Predicting and Decoding\nAllosteric Binding",
        category: "Computational Biology",
        year: "2026",
        format: "JCTC",
        description: "Protein language models and frustration landscapes reveal why allosteric kinase sites evade structure-based detection.",
        posterImage: allostericImg,
    },
    {
        id: 1, // Featured Paper
        name: "UFC Fight Prediction",
        category: "Machine Learning",
        year: "2025",
        format: "PDF",
        description: "A statistical deep-dive into predicting fight outcomes using Random Forests & Logistic Regression.",
        posterImage: boxingImg,
    },
    {
        id: 2,
        name: "AI as a Cognitive Partner",
        displayTitle: "AI as a\nCognitive Partner",
        category: "Artificial Intelligence",
        year: "2025",
        format: "PDF",
        description: "Argues for viewing AI as a 'cognitive partner' for collaborative reasoning, rather than just a tool for automation.",
        posterImage: airplaneImg,
    }
];


const PapersRow = () => {
    const location = useLocation();
    const scrollRef = useRef(null);
    const [showArrows, setShowArrows] = useState({ left: false, right: false });

    const checkScroll = () => {
        if (scrollRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
            setShowArrows({
                left: scrollLeft > 10,
                right: scrollLeft < scrollWidth - clientWidth - 10
            });
        }
    };

    useEffect(() => {
        checkScroll();
        window.addEventListener('resize', checkScroll);
        return () => window.removeEventListener('resize', checkScroll);
    }, []);

    const scroll = (direction) => {
        if (scrollRef.current) {
            const scrollAmount = 450;
            scrollRef.current.scrollBy({
                left: direction === 'left' ? -scrollAmount : scrollAmount,
                behavior: 'smooth'
            });
        }
    };

    return (
        <section id="papers" className="py-8 bg-black overflow-hidden relative group">
            <div className="px-8 md:px-12 lg:px-16">

                <h2 className="text-3xl md:text-4xl font-bold text-white mb-8">
                    Papers
                </h2>

                <div className="relative overflow-visible">
                    {/* Left Arrow */}
                    {showArrows.left && (
                        <button
                            onClick={() => scroll('left')}
                            className="absolute left-4 top-1/2 -translate-y-1/2 z-40 group"
                            aria-label="Scroll left"
                        >
                            <div className="flex items-center justify-center w-8 h-8 md:w-10 md:h-10 rounded-full bg-white/10 backdrop-blur-md border border-white/20 transition-all duration-300 group-hover:bg-white/20 group-hover:scale-110">
                                <svg className="w-4 h-4 md:w-5 md:h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                </svg>
                            </div>
                        </button>
                    )}

                    {/* Right Arrow */}
                    {showArrows.right && (
                        <button
                            onClick={() => scroll('right')}
                            className="absolute right-4 top-1/2 -translate-y-1/2 z-40 group"
                            aria-label="Scroll right"
                        >
                            <div className="flex items-center justify-center w-8 h-8 md:w-10 md:h-10 rounded-full bg-white/10 backdrop-blur-md border border-white/20 transition-all duration-300 group-hover:bg-white/20 group-hover:scale-110">
                                <svg className="w-4 h-4 md:w-5 md:h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                            </div>
                        </button>
                    )}

                    <div
                        ref={scrollRef}
                        onScroll={checkScroll}
                        className="flex gap-12 lg:gap-16 overflow-x-auto scrollbar-hide scroll-smooth pb-12 pt-4 px-4 -mx-4"
                        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                    >

                        {papers.map((paper) => (
                            <div
                                key={paper.id}
                                className="relative flex-shrink-0 w-[280px] sm:w-[350px] md:w-[400px]"
                            >
                                {/* Magazine Cover Object */}
                                <Link
                                    to={`/paper/${paper.name.toLowerCase().replace(/\s+/g, '-')}`}
                                    state={{ background: location }}
                                    className="relative block group w-full aspect-[3/4] rounded-sm transition-all duration-500 hover:-translate-y-4 hover:rotate-1 hover:z-10 perspective-1000 outline-none focus-visible:ring-2 focus-visible:ring-white/50"
                                >

                                    {/* Depth Shadows (The "Spine" and "Drop Shadow") */}
                                    <div className="absolute inset-0 bg-gray-800 rounded-sm transform translate-x-2 translate-y-2 group-hover:translate-x-5 group-hover:translate-y-5 transition-transform duration-500 ease-out -z-10 shadow-2xl" />

                                    {/* The Physical Cover */}
                                    <div className="relative h-full w-full bg-gray-900 rounded-sm overflow-hidden shadow-xl border border-white/5">

                                        {/* Cover Image */}
                                        <div
                                            className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-110"
                                            style={{ backgroundImage: paper.posterImage ? `url(${paper.posterImage})` : 'linear-gradient(to bottom, #1f2937, #111827)' }}
                                        />

                                        {/* Gradient Overlay for Readability */}
                                        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent opacity-90" />

                                        {/* Overlay Content */}
                                        <div className="absolute inset-0 p-6 flex flex-col justify-between">

                                            {/* Top Metadata Banner */}
                                            <div className="absolute top-0 left-0 right-0 bg-red-600 p-4 flex justify-between items-center z-20">
                                                <div>
                                                    <p className="text-white text-[10px] font-black tracking-[0.2em] uppercase mb-0.5">
                                                        {paper.category}
                                                    </p>
                                                    <p className="text-black/80 text-[9px] font-bold font-mono tracking-widest">
                                                        {paper.year}
                                                    </p>
                                                </div>
                                                <div className="bg-black text-white text-[9px] font-bold px-2 py-1 uppercase tracking-wider shadow-sm">
                                                    {paper.format}
                                                </div>
                                            </div>

                                            {/* Spacer to push content down below the banner */}
                                            <div className="mt-16" />

                                            {/* Bottom Content */}
                                            <div className="mt-auto space-y-4">
                                                <h1 className="text-4xl font-black text-white leading-[0.9] tracking-tighter uppercase italic drop-shadow-lg">
                                                    {(paper.displayTitle || paper.name).split("\n").map((line, lineIdx) => (
                                                        <div key={lineIdx}>
                                                            {line.split(" ").map((word, wordIdx, arr) => (
                                                                <span key={wordIdx} className={wordIdx % 2 !== 0 ? "text-transparent bg-clip-text bg-gradient-to-r from-red-600 to-white" : ""}>
                                                                    {word}{wordIdx < arr.length - 1 ? ' ' : ''}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    ))}
                                                </h1>

                                                <div className="border-l-2 border-red-600 pl-3">
                                                    <p className="text-gray-300 text-xs font-medium leading-relaxed line-clamp-3">
                                                        {paper.description}
                                                    </p>
                                                </div>

                                                <div className="block w-full bg-white/90 group-hover:bg-red-600 text-black group-hover:text-white font-black uppercase tracking-[0.15em] py-3 text-center text-xs transition-colors duration-300 mt-4 backdrop-blur-sm">
                                                    Read Paper
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </Link>


                                {/* Reflection on Floor */}
                                <div className="w-[90%] h-8 mt-4 bg-gradient-to-b from-white/5 to-transparent rounded-full blur-xl transform scale-x-75 opacity-0 group-hover:opacity-40 transition-opacity duration-500" />
                            </div>
                        ))}

                    </div>
                </div>
            </div>
        </section>
    );
};

export default PapersRow;
